// VFR navigation: great-circle geometry, the wind triangle and the
// navigation log's per-leg figures. Ported from webplan-modern's
// apps/web/src/lib/nav.ts and the recalculateRows loop in its plan page.
//
// Pure and shared: the API computes a plan's legs from its stored inputs,
// and the web app computes the same figures offline while the pilot edits,
// so the two can never disagree. No clock, no I/O, no rounding beyond what
// the navigation log prints.
//
// Conventions: positions in decimal degrees (north and east positive);
// directions in degrees 0-360; wind is the direction it blows FROM, degrees
// TRUE, as a forecast gives it; magnetic variation is east positive, so
// magnetic = true - variation; speeds in knots, distances in nautical miles.

const EARTH_RADIUS_NM = 3440.065

export const SPEED_UNITS = ['kt', 'mph', 'kmh'] as const
export type SpeedUnit = (typeof SPEED_UNITS)[number]

export const DISTANCE_UNITS = ['nm', 'sm', 'km'] as const
export type DistanceUnit = (typeof DISTANCE_UNITS)[number]

const KNOTS_PER: Record<SpeedUnit, number> = {
  kt: 1,
  mph: 0.868976,
  kmh: 0.539957,
}

// An aircraft's figures are kept in its own flight manual's unit. webplan
// used them as knots whatever the unit, so a km/h aircraft (OH-SIX, 195)
// was planned at 195 kt.
export function toKnots(speed: number, unit: SpeedUnit): number {
  return speed * KNOTS_PER[unit]
}

function toRad(degrees: number) {
  return (degrees * Math.PI) / 180
}

function toDeg(radians: number) {
  return (radians * 180) / Math.PI
}

export function normaliseDegrees(degrees: number): number {
  return ((degrees % 360) + 360) % 360
}

function round1(n: number) {
  return Math.round(n * 10) / 10
}

/** Great-circle distance in nautical miles (haversine). */
export function distanceNm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return EARTH_RADIUS_NM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

/** Initial true course from point 1 to point 2, degrees 0-360. */
export function trueCourse(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const φ1 = toRad(lat1)
  const φ2 = toRad(lat2)
  const dλ = toRad(lon2 - lon1)
  const y = Math.sin(dλ) * Math.cos(φ2)
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(dλ)
  return normaliseDegrees(toDeg(Math.atan2(y, x)))
}

export interface WindTriangleInput {
  trueCourse: number
  tasKt: number
  windDir: number
  windSpeedKt: number
  variation: number
}

export interface WindTriangleResult {
  magCourse: number
  /** Wind correction angle: positive means turn right (wind from the right). */
  windCorrection: number
  magHeading: number
  groundSpeedKt: number
}

/**
 * The VFR wind triangle. Returns null when the leg cannot be flown: no
 * airspeed, or a crosswind component stronger than the TAS (webplan let
 * Math.asin return NaN into the navigation log).
 */
export function windTriangle(input: WindTriangleInput): WindTriangleResult | null {
  if (!(input.tasKt > 0)) return null
  const relative = toRad(input.windDir - input.trueCourse)
  const crossRatio = (input.windSpeedKt * Math.sin(relative)) / input.tasKt
  if (Math.abs(crossRatio) > 1) return null
  const wca = Math.asin(crossRatio)
  const groundSpeed = input.tasKt * Math.cos(wca) - input.windSpeedKt * Math.cos(relative)
  if (groundSpeed <= 0) return null
  const magCourse = normaliseDegrees(input.trueCourse - input.variation)
  return {
    magCourse: round1(magCourse),
    windCorrection: round1(toDeg(wca)),
    magHeading: round1(normaliseDegrees(magCourse + toDeg(wca))),
    groundSpeedKt: round1(groundSpeed),
  }
}

/** Whole minutes to fly `distance` nm at `groundSpeed` kt, as the log prints them. */
export function legMinutes(distance: number, groundSpeedKt: number): number {
  if (!(groundSpeedKt > 0)) return 0
  return Math.round((distance / groundSpeedKt) * 60)
}

/** Minutes as HH:MM, e.g. 95 -> "01:35". */
export function formatHhMm(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes))
  return `${String(Math.floor(whole / 60)).padStart(2, '0')}:${String(whole % 60).padStart(2, '0')}`
}

// --- The navigation log ------------------------------------------------------

/** One point of a plan, as stored: where it is, and the leg INTO it. */
export interface PlanPoint {
  lat: number | null
  lon: number | null
  variation: number | null
  windDir: number
  windSpeedKt: number
  /** This leg's TAS override, knots. null: the plan's cruise speed. */
  tasKt: number | null
}

export type LegStatus =
  /** The first point: nothing is flown into it. */
  | 'departure'
  /** This point or the previous one has no position (an unresolved legacy code). */
  | 'no_position'
  /** Wind too strong for the TAS, or no TAS at all. */
  | 'not_flyable'
  | 'ok'

export interface Leg {
  status: LegStatus
  tasKt: number | null
  distanceNm: number | null
  trueCourse: number | null
  magCourse: number | null
  windCorrection: number | null
  magHeading: number | null
  groundSpeedKt: number | null
  minutes: number | null
}

const EMPTY_LEG = {
  distanceNm: null,
  trueCourse: null,
  magCourse: null,
  windCorrection: null,
  magHeading: null,
  groundSpeedKt: null,
  minutes: null,
} as const

/**
 * The navigation log: one Leg per point, the leg flown INTO that point.
 * `cruiseKt` is the plan's cruise TAS in knots (see plannedCruiseKt); a
 * point's own tasKt overrides it for that leg.
 */
export function computeLegs(points: PlanPoint[], cruiseKt: number | null): Leg[] {
  return points.map((point, index) => {
    const tasKt = point.tasKt ?? cruiseKt
    if (index === 0) return { status: 'departure', tasKt, ...EMPTY_LEG }
    const previous = points[index - 1]!
    if (previous.lat === null || previous.lon === null || point.lat === null || point.lon === null) {
      return { status: 'no_position', tasKt, ...EMPTY_LEG }
    }
    const distance = distanceNm(previous.lat, previous.lon, point.lat, point.lon)
    const course = trueCourse(previous.lat, previous.lon, point.lat, point.lon)
    const nav =
      tasKt === null
        ? null
        : windTriangle({
            trueCourse: course,
            tasKt,
            windDir: point.windDir,
            windSpeedKt: point.windSpeedKt,
            variation: point.variation ?? 0,
          })
    if (!nav) {
      return {
        status: 'not_flyable',
        tasKt,
        ...EMPTY_LEG,
        distanceNm: round1(distance),
        trueCourse: round1(course),
      }
    }
    return {
      status: 'ok',
      tasKt,
      distanceNm: round1(distance),
      trueCourse: round1(course),
      ...nav,
      minutes: legMinutes(distance, nav.groundSpeedKt),
    }
  })
}

export interface PlanTotals {
  distanceNm: number
  /** Sum of the legs' printed minutes, as a pilot adds up the log. */
  minutes: number
  /** Legs that could not be computed: the totals leave them out. */
  incompleteLegs: number
}

export function planTotals(legs: Leg[]): PlanTotals {
  let distance = 0
  let minutes = 0
  let incompleteLegs = 0
  for (const leg of legs) {
    if (leg.status === 'departure') continue
    if (leg.status !== 'ok') incompleteLegs += 1
    distance += leg.distanceNm ?? 0
    minutes += leg.minutes ?? 0
  }
  return { distanceNm: round1(distance), minutes, incompleteLegs }
}

export interface CruiseSpeedSource {
  speedUnit: SpeedUnit
  cruiseSpeed: number
  manualCruiseSpeed: number | null
  /** The member's own planning speed for this aircraft, if they set one. */
  memberOverride: number | null
}

/**
 * The plan's cruise TAS in knots: the member's own figure for this aircraft,
 * else the aircraft's manual planning speed, else its published cruise
 * speed -- each in the aircraft's speed unit. null when none is set.
 */
export function plannedCruiseKt(source: CruiseSpeedSource): number | null {
  const speed = source.memberOverride ?? source.manualCruiseSpeed ?? (source.cruiseSpeed > 0 ? source.cruiseSpeed : null)
  return speed === null ? null : round1(toKnots(speed, source.speedUnit))
}

// --- Coordinates as the charts print them ------------------------------------

function toDms(value: number, positive: string, negative: string): string {
  const direction = value >= 0 ? positive : negative
  const abs = Math.abs(value)
  let degrees = Math.floor(abs)
  let minutes = Math.floor((abs - degrees) * 60)
  let seconds = Math.round((abs - degrees - minutes / 60) * 3600)
  if (seconds === 60) {
    seconds = 0
    minutes += 1
  }
  if (minutes === 60) {
    minutes = 0
    degrees += 1
  }
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${direction} ${degrees}°${pad(minutes)}'${pad(seconds)}"`
}

/** 61.4978 -> N 61°29'52" */
export function latToDms(lat: number): string {
  return toDms(lat, 'N', 'S')
}

/** 23.7603 -> E 23°45'37" */
export function lonToDms(lon: number): string {
  return toDms(lon, 'E', 'W')
}

/** Degrees and decimal minutes, as a chart gives them, to decimal degrees. */
export function degMinToDecimal(degrees: number, minutes: number, direction: 'N' | 'S' | 'E' | 'W'): number {
  const decimal = degrees + minutes / 60
  return direction === 'S' || direction === 'W' ? -decimal : decimal
}
